import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import Header from "../../components/layout/Header";
import Footer from "../../components/layout/Footer";
import { useCart } from "../../context/CartContext";
import { formatPrice } from "../../data/pricing";
import { initiatePayment, submitLeadForm } from "../../utils/payment";
import { BRAND } from "../../data/brand";

function Checkout() {
  const navigate = useNavigate();
  const { cartItem, clearCart, loadCart } = useCart();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
    loadCart();
  }, [loadCart]);

  useEffect(() => {
    if (!cartItem) {
      const saved = localStorage.getItem("eversky_cart");
      if (!saved) {
        navigate("/pricing");
      }
    }
  }, [cartItem, navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    if (!cartItem) return;

    setStatus("processing");
    setError("");

    submitLeadForm(
      { ...formData, service: cartItem.name, amount: cartItem.price },
      "purchase"
    );

    await initiatePayment({
      amount: cartItem.price,
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      description: `${cartItem.name} - Ever Sky Overseas`,
      onSuccess: (response) => {
        setStatus("success");
        submitLeadForm(
          {
            ...formData,
            service: cartItem.name,
            amount: cartItem.price,
            paymentId: response.razorpay_payment_id,
          },
          "payment_success"
        );
        clearCart();
      },
      onFailure: (err) => {
        setStatus("idle");
        setError(err.message);
      },
    });
  };

  if (status === "success") {
    return (
      <>
        <Header />
        <div className="layout-container layout-container--narrow layout-section text-center">
          <div className="text-6xl mb-6">🎉</div>
          <h1 id="heading-large">Payment Successful!</h1>
          <p className="mt-4 text-gray-600 text-lg">
            Thank you for choosing Ever Sky Overseas. Our team will contact you
            within 24 hours to get started on your {cartItem?.name || "service"}.
          </p>
          <p className="mt-2 text-gray-500">
            A confirmation has been sent to {formData.email}.
          </p>
          <div className="mt-8 flex gap-4 justify-center">
            <Link
              to="/"
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Back to Home
            </Link>
            <Link
              to="/contact"
              className="px-6 py-3 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50"
            >
              Contact Us
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const item = cartItem || JSON.parse(localStorage.getItem("eversky_cart") || "null");
  if (!item) return null;

  return (
    <>
      <Header />
      <div className="layout-container layout-section">
        <h1 id="heading-large" className="mb-8">
          Checkout
        </h1>

        <div className="grid md:grid-cols-2 gap-8">
          <div className="bg-slate-50 rounded-xl p-6">
            <h2 id="heading-md" className="mb-4">
              Order Summary
            </h2>
            <div className="border-b pb-4 mb-4">
              <p className="font-semibold text-lg">{item.name}</p>
              <p className="text-gray-500 capitalize">{item.type} package</p>
            </div>
            <div className="flex justify-between text-xl font-bold">
              <span>Total</span>
              <span className="text-blue-600">{formatPrice(item.price)}</span>
            </div>
            <p className="text-sm text-gray-500 mt-4">
              Secure payment powered by Razorpay. GST included where applicable.
            </p>
          </div>

          <form onSubmit={handlePayment} className="space-y-4">
            <h2 id="heading-md">Your Details</h2>
            <input
              type="text"
              name="name"
              placeholder="Full Name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full p-3 border rounded-lg"
            />
            <input
              type="email"
              name="email"
              placeholder="Email Address"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full p-3 border rounded-lg"
            />
            <input
              type="tel"
              name="phone"
              placeholder="Phone Number"
              value={formData.phone}
              onChange={handleChange}
              required
              className="w-full p-3 border rounded-lg"
            />
            <textarea
              name="message"
              placeholder="Any specific requirements? (optional)"
              value={formData.message}
              onChange={handleChange}
              rows={3}
              className="w-full p-3 border rounded-lg"
            />
            <div className="flex items-start gap-2">
              <input type="checkbox" id="terms" required className="mt-1" />
              <label htmlFor="terms" className="text-sm text-gray-600">
                I agree to the{" "}
                <Link to="/terms" className="text-blue-600 underline">
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link to="/privacy" className="text-blue-600 underline">
                  Privacy Policy
                </Link>
              </label>
            </div>

            {error && (
              <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">
                {error}
                {error.includes("not configured") && (
                  <p className="mt-2">
                    Meanwhile, contact us at{" "}
                    <a href={`tel:${BRAND.phone}`} className="underline">
                      {BRAND.phone}
                    </a>{" "}
                    or{" "}
                    <a
                      href={`https://wa.me/${BRAND.whatsapp.replace("+", "")}`}
                      className="underline"
                      target="_blank"
                      rel="noreferrer"
                    >
                      WhatsApp
                    </a>{" "}
                    to complete your purchase.
                  </p>
                )}
              </div>
            )}

            <button
              type="submit"
              disabled={status === "processing"}
              className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold disabled:opacity-50"
            >
              {status === "processing"
                ? "Processing..."
                : `Pay ${formatPrice(item.price)}`}
            </button>
          </form>
        </div>
      </div>
      <Footer />
    </>
  );
}

export default Checkout;
